import React, { useCallback, useEffect, useState } from 'react';
import { TopHeading, TopHeader } from 'modules/Corporates/Style/style';
import { SectionWrapper, NavWrapper } from 'modules/Corporates/Style/style';
import { StyledTable } from 'components/TableStyles/TableStyles';
import TableSearchFilter from 'components/UIComponents/TableSearchFilter'
import { Pagination, Collapse, Spin, Empty, Tag, message, Modal } from 'antd'

import Button from 'components/Button/index'
import { FileTextOutlined } from '@ant-design/icons';
import { EmptyWrapper } from 'modules/Corporates/Style/style';
import candidateRequest from 'utils/candidateRequest';

const { Panel } = Collapse;
const TABLE_PAGE_SIZE = 10;

// ✅ Status Color
const statusTagColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'green';
  if (s === 'failed') return 'red';
  if (s === 'in_progress') return 'blue';
  if (s === 'skipped') return 'orange';
  return 'default';
};

const CandidatesRaw = () => {
  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [sheetDataMap, setSheetDataMap] = useState({});
  const [sheetLoadingMap, setSheetLoadingMap] = useState({});
  const [searchMap, setSearchMap] = useState({});

  const [clearLoading, setClearLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [sheetNormalizingMap, setSheetNormalizingMap] = useState({});

  const [pageSize, setPageSize] = useState(10);

  const [showClearDropdown, setShowClearDropdown] = useState(false);

  // =========================
  // FETCH SHEETS
  // =========================
  const fetchRawCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await candidateRequest.get('/api/candidates/raw', {
        params: { page, limit: pageSize },
      });
      setSheets(resp?.data?.data || []);
      setTotal(resp?.data?.total || 0);
    } catch (e) {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchRawCandidates();
  }, [fetchRawCandidates]);

  // =========================
  // FETCH SHEET RECORDS
  // =========================
  const fetchSheetRecords = async (sheetId) => {
    if (sheetDataMap[sheetId]) return;

    try {
      setSheetLoadingMap(prev => ({ ...prev, [sheetId]: true }));

      const resp = await candidateRequest.get(
        `/api/candidates/raw-by-sheet/${sheetId}`,
        {
          params: { page: 1, limit: 100 }
        }
      );

      setSheetDataMap(prev => ({
        ...prev,
        [sheetId]: resp.data.data
      }));

    } catch (e) {
      message.error('Failed to load records');
    } finally {
      setSheetLoadingMap(prev => ({ ...prev, [sheetId]: false }));
    }
  };

  // =========================
  // ACTIONS
  // =========================
  const handleClearAll = () => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'This will clear all candidate data',
      onOk: async () => {
        try {
          setClearLoading(true);
          await candidateRequest.delete('/api/candidates/clear-all');
          message.success('Cleared successfully');
          fetchRawCandidates();
        } catch {
          message.error('Failed');
        } finally {
          setClearLoading(false);
        }
      },
    });
  };

  const handleIngest = async () => {
    try {
      setIngestLoading(true);
      await candidateRequest.post('/api/candidates/ingest');

      // Poll until background ingest finishes
      await new Promise((resolve) => {
        const poll = setInterval(async () => {
          try {
            const status = await candidateRequest.get('/api/candidates/ingest/status');
            if (!status?.data?.running) {
              clearInterval(poll);
              resolve();
            }
          } catch {
            clearInterval(poll);
            resolve();
          }
        }, 3000);
      });

      message.success('Ingest done');
      fetchRawCandidates();
    } catch {
      message.error('Ingest failed');
    } finally {
      setIngestLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRawCandidates();
    setRefreshing(false);
  };

  const handleNormalizeSheet = async (sheetId) => {
    try {
      setSheetNormalizingMap(p => ({ ...p, [sheetId]: true }));
      await candidateRequest.post(`/api/candidates/normalize-sheet/${sheetId}`);
      message.success('Normalization started');
      setTimeout(fetchRawCandidates, 3000);
    } catch {
      message.error('Failed');
    } finally {
      setSheetNormalizingMap(p => ({ ...p, [sheetId]: false }));
    }
  };

  // =========================
  // SEARCH
  // =========================
  const handleSearch = (sheetId, value) => {
    setSearchMap(prev => ({ ...prev, [sheetId]: value }));
  };

  // =========================
  // TABLE BUILDER
  // =========================
  const getTable = (records) => {
    if (!records?.length) return { columns: [], data: [] };

    const parsed = records.map((r, i) => ({
      key: r.candidate_id || i,
      normalization_status: r.normalization_status,
      ...r.raw_data
    }));

    const keys = new Set();
    parsed.forEach(row => Object.keys(row).forEach(k => k !== 'key' && keys.add(k)));

    const columns = [
      {
        title: 'Status',
        dataIndex: 'normalization_status',
        fixed: 'left',
        width: 140,
        render: (val) => <Tag color={statusTagColor(val)}>{val}</Tag>
      },
      ...Array.from(keys).filter(k => k !== 'normalization_status').map(key => ({
        title: key,
        dataIndex: key,
        width: 180,
        ellipsis: true,
        render: (val) => typeof val === 'object' ? JSON.stringify(val) : val ?? '-'
      }))
    ];

    return { columns, data: parsed };
  };

  return (
    <SectionWrapper column>
      {/* <TopHeader row spaceBetween alignCenter>
        <TopHeading>Candidates Raw</TopHeading>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button.Primary text="Clear Data" onClick={handleClearAll} loading={clearLoading} />
          <Button.Primary text="Ingest" onClick={handleIngest} loading={ingestLoading} />
          <Button.Secondary text="Refresh" onClick={handleRefresh} loading={refreshing} />
        </div>
      </TopHeader> */}

      <TopHeader row spaceBetween alignCenter>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
          <TopHeading>Candidates Raw</TopHeading>
          <span
            onClick={() => setShowClearDropdown(prev => !prev)}
            style={{
              cursor: 'pointer',
              fontSize: 12,
              color: '#8c8c8c',
              userSelect: 'none',
              display: 'inline-block',
              transform: showClearDropdown ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            ▼
          </span>
          {showClearDropdown && (
            <Button.Primary text="Clear Data" onClick={() => { setShowClearDropdown(false); handleClearAll(); }} loading={clearLoading} />
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button.Primary text="Ingest" onClick={handleIngest} loading={ingestLoading} />
          <Button.Secondary text="Refresh" onClick={handleRefresh} loading={refreshing} />
        </div>
      </TopHeader>

      <NavWrapper column>
        {loading ? (
          <EmptyWrapper center><Spin /></EmptyWrapper>
        ) : sheets.length === 0 ? (
          <EmptyWrapper center><Empty description="No data" /></EmptyWrapper>
        ) : (
          <>
            <Collapse
              accordion
              expandIconPosition="end"
              bordered={false}
              style={{
                background: 'transparent',
                padding: '16px'
              }}
              onChange={(key) => key && fetchSheetRecords(key)}
              expandIcon={({ isActive, panelKey }) => {
                const sheet = sheets.find(s => s.sheet_id === panelKey);

                // ❌ Hide icon if no skipped records
                if (!sheet || sheet.skipped_count <= 0) return null;

                return (
                  <span
                    style={{
                      display: 'inline-block',
                      transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: '0.2s'
                    }}
                  >
                    ▶
                  </span>
                );
              }}
            >
              {sheets.map(sheet => {
                const records = sheetDataMap[sheet.sheet_id] || [];
                const searchValue = searchMap[sheet.sheet_id] || '';

                const filtered = records.filter(r => {
                  const raw = r.raw_data || {};
                  const search = searchValue.toLowerCase();

                  // ✅ Flatten important fields only
                  const valuesToSearch = [
                    raw["First Name"],
                    raw["Last Name"],
                    raw["Email"],
                    raw["Email ID"],
                    raw["Phone Number"]
                  ];

                  return valuesToSearch.some(val =>
                    String(val || '').toLowerCase().includes(search)
                  );
                });

                const table = getTable(filtered);

                return (
                  <Panel
                    key={sheet.sheet_id}
                    collapsible={sheet.skipped_count > 0 ? '' : 'disabled'}
                    style={{
                      marginBottom: 18,
                      borderRadius: 12,
                      background: '#ffffff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      border: '1px solid #f0f0f0',
                      padding: '10px 12px' // ✅ FULL GAP FIX
                    }}
                    header={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'space-between', // ✅ KEY FIX
                          paddingRight: 30 // ✅ SPACE for collapse arrow
                        }}
                      >
                        {/* LEFT SIDE */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>

                          {/* ICON */}
                          <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: '#e6f4ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FileTextOutlined style={{ color: '#1677ff' }} />
                          </div>

                          {/* INFO */}
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {sheet.source_file}
                              <span style={{ color: '#8c8c8c', marginLeft: 6 }}>
                                ({sheet.source_sheet})
                              </span>
                            </div>

                            {/* STATUS TAGS */}
                            <div
                              style={{
                                marginTop: 6,
                                display: 'flex',
                                gap: 6,
                                flexWrap: 'wrap'
                              }}
                            >
                              {sheet.pending_count > 0 && (
                                <Tag>Pending: {sheet.pending_count}</Tag>
                              )}

                              {sheet.completed_count > 0 && (
                                <Tag color="green">Completed: {sheet.completed_count}</Tag>
                              )}

                              {sheet.failed_count > 0 && (
                                <Tag color="red">Failed: {sheet.failed_count}</Tag>
                              )}

                              {sheet.skipped_count > 0 && (
                                <Tag color="orange">Skipped: {sheet.skipped_count}</Tag>
                              )}

                              {sheet.in_progress_count > 0 && (
                                <Tag color="blue">In Progress: {sheet.in_progress_count}</Tag>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* RIGHT SIDE (Date + Normalize Button) */}
                        <div style={{ flexShrink: 0, marginRight: 15, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          
                          {/* DATE & TIME - now above the button */}
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            {sheet.updated_at
                              ? new Date(sheet.updated_at).toLocaleString('en-IN', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })
                              : '-'}
                          </div>

                          {/* NORMALIZE BUTTON */}
                          {/* {(() => {
                            const pending = sheet.pending_count || 0;
                            const inProgress = sheet.in_progress_count || 0;
                            const completed = sheet.completed_count || 0;
                            const isNormalizing = sheetNormalizingMap[sheet.sheet_id] || inProgress > 0;
                            const isNormalized = pending === 0 && inProgress === 0 && completed > 0;
                            const btnText = isNormalizing ? 'Normalizing...' : isNormalized ? 'Normalized' : 'Normalize';
                            return (
                              <Button.Primary
                                height="30px"
                                text={btnText}
                                disabled={isNormalizing || isNormalized}
                                loading={isNormalizing}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNormalizeSheet(sheet.sheet_id);
                                }}
                              />
                            );
                          })()} */}
                        </div>
                      </div>
                    }
                  >
                    <div style={{ padding: '12px' }}> {/* ✅ INNER CONTENT GAP */}

                      <TableSearchFilter
                        placeholder="Search name/email"
                        searchValue={searchValue}
                        setSearchValue={(val) => handleSearch(sheet.sheet_id, val)}
                      />

                      {sheetLoadingMap[sheet.sheet_id] ? (
                        <Spin />
                      ) : (
                        <StyledTable
                          columns={table.columns}
                          dataSource={table.data}
                          rowKey="key"
                          scroll={{ x: 1200, y: 400 }}
                          pagination={{ pageSize: TABLE_PAGE_SIZE }}
                        />
                      )}
                    </div>
                  </Panel>
                );
              })}
            </Collapse>

            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              // showSizeChanger={false}
              // onChange={(p) => setPage(p)}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(p, size) => {
                setPage(p);
                if (size !== pageSize) {
                  setPageSize(size);
                  setPage(1); // reset to page 1 when size changes
                }
              }}
              onShowSizeChange={(current, size) => {
                setPageSize(size);
                setPage(1);
              }}
              style={{ marginTop: 20, textAlign: 'center' }}
            />

          </>
        )}
      </NavWrapper>
    </SectionWrapper>
  );
};

export default CandidatesRaw;